import { useState, useEffect } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  Cpu,
  Database,
  HardDrive,
  RefreshCw,
  Server,
  Wallet,
  XCircle,
  Bell,
  BellOff,
  ExternalLink,
  Copy,
  Zap,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  useNodeHealth,
  useSystemAlerts,
  useAcknowledgeAlert,
  useResolveAlert,
} from '@/hooks/use-admin';

function StatusBadge({ status }) {
  const variants = {
    healthy: {
      variant: 'default',
      className: 'bg-emerald-600 hover:bg-emerald-700',
      icon: CheckCircle,
    },
    warning: {
      variant: 'secondary',
      className: 'bg-amber-600 hover:bg-amber-700',
      icon: AlertTriangle,
    },
    critical: {
      variant: 'destructive',
      className: 'bg-rose-600 hover:bg-rose-700',
      icon: XCircle,
    },
    low: {
      variant: 'secondary',
      className: 'bg-amber-600 hover:bg-amber-700',
      icon: AlertTriangle,
    },
    empty: {
      variant: 'destructive',
      className: 'bg-rose-600 hover:bg-rose-700',
      icon: XCircle,
    },
  };

  const config = variants[status] || variants.warning;
  const Icon = config.icon;

  return (
    <Badge className={`${config.className} text-white`}>
      <Icon className="w-3 h-3 mr-1" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

function CopyButton({ text }) {
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={handleCopy}
          >
            <Copy className="h-3 w-3" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Copy address</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function WalletCard({ wallet }) {
  if (!wallet) return null;

  const shortAddress = `${wallet.address.slice(0, 10)}...${wallet.address.slice(
    -8
  )}`;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Wallet className="h-5 w-5" />
          DKG Operational Wallet
        </CardTitle>
        <CardDescription className="flex items-center gap-1">
          <code className="text-xs">{shortAddress}</code>
          <CopyButton text={wallet.address} />
          <a
            href={`https://sepolia.basescan.org/address/${wallet.address}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-600"
          >
            <ExternalLink className="h-3 w-3" />
          </a>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* ETH Balance */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">ETH Balance</span>
            <StatusBadge status={wallet.eth.status} />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold">
              {wallet.eth.balance.toFixed(6)}
            </span>
            <span className="text-muted-foreground">ETH</span>
          </div>
          <Progress
            value={Math.min((wallet.eth.balance / 1) * 100, 100)}
            className="h-2"
          />
          <p className="text-xs text-muted-foreground">
            Minimum: {wallet.eth.minimum} ETH
          </p>
        </div>

        {/* TRAC Balance */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">TRAC Balance</span>
            <StatusBadge status={wallet.trac.status} />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold">
              {wallet.trac.balance.toFixed(2)}
            </span>
            <span className="text-muted-foreground">TRAC</span>
          </div>
          <Progress
            value={Math.min((wallet.trac.balance / 100) * 100, 100)}
            className="h-2"
          />
          <p className="text-xs text-muted-foreground">
            Minimum: {wallet.trac.minimum} TRAC
          </p>
        </div>

        {/* Allowance */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">TRAC Allowance</span>
            <StatusBadge status={wallet.allowance.status} />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold">
              {wallet.allowance.amount.toFixed(2)}
            </span>
            <span className="text-muted-foreground">TRAC approved</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Hub Contract: {wallet.allowance.spender.slice(0, 10)}...
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function NodeStatusCard({ node }) {
  if (!node) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Server className="h-5 w-5" />
          DKG Node
        </CardTitle>
        <CardDescription>{node.endpoint}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Status</span>
          <StatusBadge status={node.online ? 'healthy' : 'critical'} />
        </div>

        {node.online && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Version</span>
              <Badge variant="outline">{node.version}</Badge>
            </div>
          </>
        )}

        {!node.online && (
          <div className="p-3 bg-rose-500/10 dark:bg-rose-500/20 rounded-lg border border-rose-500/20">
            <p className="text-sm text-rose-600 dark:text-rose-400">
              {node.error || 'Node is offline'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function QueueStatsCard({ queue }) {
  if (!queue || queue.error) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Database className="h-5 w-5" />
          Job Queue
        </CardTitle>
        <CardDescription>
          Concurrency: {queue.concurrency} workers
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-2 bg-sky-500/10 dark:bg-sky-500/20 rounded-lg border border-sky-500/20">
            <div className="text-2xl font-bold text-sky-600 dark:text-sky-400">
              {queue.jobs.waiting}
            </div>
            <div className="text-xs text-muted-foreground">Waiting</div>
          </div>
          <div className="text-center p-2 bg-amber-500/10 dark:bg-amber-500/20 rounded-lg border border-amber-500/20">
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
              {queue.jobs.active}
            </div>
            <div className="text-xs text-muted-foreground">Active</div>
          </div>
          <div className="text-center p-2 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-lg border border-emerald-500/20">
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {queue.jobs.completed}
            </div>
            <div className="text-xs text-muted-foreground">Completed</div>
          </div>
          <div className="text-center p-2 bg-rose-500/10 dark:bg-rose-500/20 rounded-lg border border-rose-500/20">
            <div className="text-2xl font-bold text-rose-600 dark:text-rose-400">
              {queue.jobs.failed}
            </div>
            <div className="text-xs text-muted-foreground">Failed</div>
          </div>
          <div className="text-center p-2 bg-violet-500/10 dark:bg-violet-500/20 rounded-lg border border-violet-500/20">
            <div className="text-2xl font-bold text-violet-600 dark:text-violet-400">
              {queue.jobs.delayed}
            </div>
            <div className="text-xs text-muted-foreground">Delayed</div>
          </div>
          <div className="text-center p-2 bg-slate-500/10 dark:bg-slate-500/20 rounded-lg border border-slate-500/20">
            <div className="text-2xl font-bold">{queue.workers}</div>
            <div className="text-xs text-muted-foreground">Workers</div>
          </div>
        </div>

        {queue.isPaused && (
          <div className="mt-4 p-3 bg-amber-500/10 dark:bg-amber-500/20 rounded-lg border border-amber-500/20">
            <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Queue is paused
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AlertsSection() {
  const { data, isLoading } = useSystemAlerts({ resolved: false });
  const acknowledgeAlert = useAcknowledgeAlert();
  const resolveAlert = useResolveAlert();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  const alerts = data?.alerts || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          System Alerts
          {alerts.length > 0 && (
            <Badge variant="destructive">{alerts.length}</Badge>
          )}
        </CardTitle>
        <CardDescription>Active system alerts and warnings</CardDescription>
      </CardHeader>
      <CardContent>
        {alerts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
            <p>No active alerts</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Severity</TableHead>
                <TableHead>Alert</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {alerts.map((alert) => (
                <TableRow key={alert._id}>
                  <TableCell>
                    <Badge
                      variant={
                        alert.severity === 'critical'
                          ? 'destructive'
                          : alert.severity === 'warning'
                          ? 'secondary'
                          : 'outline'
                      }
                    >
                      {alert.severity}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{alert.title}</p>
                      <p className="text-sm text-muted-foreground truncate max-w-md">
                        {alert.message}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(alert.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {!alert.acknowledged && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => acknowledgeAlert.mutate(alert._id)}
                          disabled={acknowledgeAlert.isLoading}
                        >
                          <BellOff className="h-3 w-3 mr-1" />
                          Ack
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => resolveAlert.mutate(alert._id)}
                        disabled={resolveAlert.isLoading}
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Resolve
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function IssuesSection({ issues }) {
  if (!issues || issues.length === 0) return null;

  return (
    <Card className="border-amber-500/50">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-5 w-5" />
          Issues Detected
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {issues.map((issue, index) => (
            <li
              key={index}
              className={`flex items-center gap-2 p-2 rounded border ${
                issue.severity === 'critical'
                  ? 'bg-rose-500/10 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400 border-rose-500/20'
                  : 'bg-amber-500/10 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/20'
              }`}
            >
              {issue.severity === 'critical' ? (
                <XCircle className="h-4 w-4" />
              ) : (
                <AlertTriangle className="h-4 w-4" />
              )}
              <span className="text-sm">{issue.message}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export default function AdminNodeHealthPage() {
  const { data, isLoading, refetch, isFetching, dataUpdatedAt } =
    useNodeHealth();
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState(10);

  // Countdown timer for next refresh
  useEffect(() => {
    const interval = setInterval(() => {
      if (dataUpdatedAt) {
        const elapsed = Math.floor((Date.now() - dataUpdatedAt) / 1000);
        const remaining = Math.max(0, 10 - elapsed);
        setSecondsUntilRefresh(remaining);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [dataUpdatedAt]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-24" />
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6" />
            Node Health
          </h1>
          <p className="text-muted-foreground">
            Monitor your DKG node, wallet balances, and system health
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>
              {isFetching ? (
                'Updating...'
              ) : (
                <>Refreshes in {secondsUntilRefresh}s</>
              )}
            </span>
          </div>
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overall Status */}
      {data && (
        <Card
          className={`border-2 ${
            data.status === 'healthy'
              ? 'border-emerald-500/50 bg-emerald-500/5 dark:bg-emerald-500/10'
              : data.status === 'warning'
              ? 'border-amber-500/50 bg-amber-500/5 dark:bg-amber-500/10'
              : 'border-rose-500/50 bg-rose-500/5 dark:bg-rose-500/10'
          }`}
        >
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {data.status === 'healthy' ? (
                  <CheckCircle className="h-8 w-8 text-emerald-500" />
                ) : data.status === 'warning' ? (
                  <AlertTriangle className="h-8 w-8 text-amber-500" />
                ) : (
                  <XCircle className="h-8 w-8 text-rose-500" />
                )}
                <div>
                  <h2 className="text-xl font-semibold">
                    System Status:{' '}
                    {data.status.charAt(0).toUpperCase() + data.status.slice(1)}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {data.blockchain.network} • Chain ID:{' '}
                    {data.blockchain.chainId}
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="text-lg px-4 py-1">
                <Zap className="h-4 w-4 mr-1" />
                {data.queue?.jobs?.active || 0} Active Jobs
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Issues */}
      <IssuesSection issues={data?.issues} />

      {/* Main Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <WalletCard wallet={data?.wallet} />
        <NodeStatusCard node={data?.node} />
        <QueueStatsCard queue={data?.queue} />
      </div>

      {/* Alerts */}
      <AlertsSection />
    </div>
  );
}
